import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { api } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import { useTenant } from "../../../contexts/TenantContext.tsx";
import CenteredSpinner from "../../../components/CenteredSpinner.tsx";
import ImageUploadField from "../../../components/ImageUploadField.tsx";
import TenantAvatar from "../../../components/TenantAvatar.tsx";

const LOGO_TYPES = ["image/png", "image/jpeg", "image/svg+xml", "image/webp"];

function LogoPage() {
  const { selectedTenantId } = useTenant();
  const queryClient = useQueryClient();

  const tenant = useQuery({
    queryKey: keys.tenant.detail(selectedTenantId ?? ""),
    queryFn: () => api.getTenant(selectedTenantId!),
    enabled: !!selectedTenantId,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: keys.tenant.detail(selectedTenantId!) });
    queryClient.invalidateQueries({ queryKey: keys.tenant.all });
  };

  const uploadMutation = useMutation({
    mutationFn: (file: File) => api.uploadTenantLogo(selectedTenantId!, file),
    onSuccess: () => {
      refresh();
      toast.success("Logo uploaded");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteTenantLogo(selectedTenantId!),
    onSuccess: () => {
      refresh();
      toast.success("Logo removed");
    },
  });

  if (tenant.isLoading) return <CenteredSpinner />;

  // `logo_url` is the logo endpoint with a per-upload version, so it busts caches.
  const logoUrl = tenant.data?.logo_url ?? null;

  return (
    <ImageUploadField
      preview={<TenantAvatar name={tenant.data?.name ?? ""} logoUrl={logoUrl} size={64} />}
      hasImage={!!logoUrl}
      accept={LOGO_TYPES}
      caption="PNG, JPG, SVG or WebP, up to 2 MB"
      onUpload={uploadMutation.mutateAsync}
      onRemove={deleteMutation.mutateAsync}
      removeConfirm={{
        title: "Remove logo",
        body: (
          <>
            Remove the logo of <strong>{tenant.data?.name}</strong>? Its initials are shown instead.
          </>
        ),
      }}
    />
  );
}

export default LogoPage;
